<template>
  <transition name="fade">
    <div v-if="warningVisible" class="session-timeout-layer">
      <div class="session-timeout-modal">
        <h3>{{ $t('session.timeout.title') }}</h3>
        <p v-html="$t('session.timeout.description', { seconds: countdownSeconds })"></p>
        <div class="session-timeout-actions">
          <button class="btn btn-secondary" @click="handleLogout">{{ $t('session.timeout.btn_logout') }}</button>
          <button class="btn btn-primary" @click="handleContinue">{{ $t('session.timeout.btn_continue') }}</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import {secureStorage} from "@/services/secureStorage";
import router from "@/router/router";
import echoService from "@/services/echoService";
const DEFAULT_TIMEOUT_SECONDS = 120;
const WARNING_LEEWAY_SECONDS = 30;
const PING_SAFETY_SECONDS = 5;
const ACTIVITY_EVENTS = ['click', 'keydown', 'mousemove', 'touchstart', 'scroll'];
const USER_STATUS_EVENT = 'UserSessionTerminated';
const USER_STATUS_CHANNEL_PREFIX = 'users.status.';

export default {
  name: 'SessionTimeoutManager',
  data() {
    return {
      warningVisible: false,
      countdownSeconds: WARNING_LEEWAY_SECONDS,
      lastActivityAt: Date.now(),
      timeoutSeconds: DEFAULT_TIMEOUT_SECONDS,
      nextRefreshSeconds: DEFAULT_TIMEOUT_SECONDS,
      logoutTimeoutSeconds: DEFAULT_TIMEOUT_SECONDS,
      pingTimerId: null,
      activityTimerId: null,
      userStatusChannelName: null,
    };
  },
  computed: {
    isAuthenticated() {
      return this.$store.state.user.isAuthenticated;
    },
    authToken() {
      return this.$store.state.user.token || secureStorage.getItem('setIn');
    },
    currentUserId() {
      return this.$store.state.user?.user?.id || null;
    },
    clientDeviceHeader() {
      if (navigator?.userAgent) {
        return navigator.userAgent;
      }
      return 'SlacklineTJS-Web';
    },
    warningThresholdMs() {
      const threshold = Math.max(this.timeoutSeconds - WARNING_LEEWAY_SECONDS, WARNING_LEEWAY_SECONDS);
      return threshold * 1000;
    },
    timeoutMs() {
      return this.timeoutSeconds * 1000;
    }
  },
  watch: {
    isAuthenticated(value) {
      if (value) {
        this.startTracking();
      } else {
        this.stopTracking();
      }
    },
    currentUserId(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }
      if (newValue && this.isAuthenticated) {
        this.subscribeUserStatusChannel();
      } else {
        this.leaveUserStatusChannel();
      }
    }
  },
  mounted() {
    if (this.isAuthenticated) {
      this.startTracking();
    }
  },
  beforeUnmount() {
    this.stopTracking();
  },
  methods: {
    startTracking() {
      this.resetActivity();
      this.registerActivityListeners();
      this.scheduleActivityCheck();
      this.schedulePresencePing(0);
      if (typeof echoService.refreshAuthHeaders === 'function') {
        echoService.refreshAuthHeaders();
      }
      this.subscribeUserStatusChannel();
    },
    stopTracking() {
      this.unregisterActivityListeners();
      if (this.activityTimerId) {
        clearInterval(this.activityTimerId);
        this.activityTimerId = null;
      }
      if (this.pingTimerId) {
        clearTimeout(this.pingTimerId);
        this.pingTimerId = null;
      }
      this.leaveUserStatusChannel();
      this.warningVisible = false;
    },
    registerActivityListeners() {
      ACTIVITY_EVENTS.forEach(eventName => {
        window.addEventListener(eventName, this.handleActivity, { passive: true });
      });
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    },
    unregisterActivityListeners() {
      ACTIVITY_EVENTS.forEach(eventName => {
        window.removeEventListener(eventName, this.handleActivity);
      });
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    },
    handleActivity() {
      this.resetActivity();
      if (this.warningVisible) {
        this.warningVisible = false;
      }
    },
    handleVisibilityChange() {
      if (!document.hidden) {
        this.handleActivity();
      }
    },
    resetActivity() {
      this.lastActivityAt = Date.now();
    },
    scheduleActivityCheck() {
      if (this.activityTimerId) {
        clearInterval(this.activityTimerId);
      }
      this.activityTimerId = setInterval(this.evaluateInactivity, 1000);
    },
    evaluateInactivity() {
      const now = Date.now();
      const idleMs = now - this.lastActivityAt;
      if (idleMs >= this.timeoutMs) {
        this.warningVisible = false;
        this.handleLogout();
        return;
      }
      if (idleMs >= this.warningThresholdMs) {
        const remaining = Math.max(Math.ceil((this.timeoutMs - idleMs) / 1000), 0);
        this.countdownSeconds = remaining;
        this.warningVisible = true;
      } else {
        this.warningVisible = false;
      }
    },
    schedulePresencePing(delaySeconds) {
      if (this.pingTimerId) {
        clearTimeout(this.pingTimerId);
      }
      const delay = Math.max(delaySeconds, 0);
      this.pingTimerId = setTimeout(this.sendPresencePing, delay * 1000);
    },
    async sendPresencePing({ resetActivity = false } = {}) {
      if (!this.isAuthenticated) {
        return;
      }
      if (!this.authToken) {
        console.warn('⚠️ Presence ping omitido: no hay token disponible.');
        return;
      }
      try {
        const response = await this.$axios.post('/presence/ping', {}, {
          withCredentials: true,
          headers: {
            Accept: 'application/json',
            'X-Client-Device': this.clientDeviceHeader,
            Authorization: `Bearer ${this.authToken}`,
          }
        });
        const data = response?.data || {};
        const logoutWindow = data.logout_timeout_seconds || data.timeout_seconds || DEFAULT_TIMEOUT_SECONDS;
        this.logoutTimeoutSeconds = logoutWindow;
        this.timeoutSeconds = logoutWindow;
        this.nextRefreshSeconds = data.next_refresh_after_seconds || DEFAULT_TIMEOUT_SECONDS;
        const nextDelay = Math.max((this.nextRefreshSeconds || DEFAULT_TIMEOUT_SECONDS) - PING_SAFETY_SECONDS, WARNING_LEEWAY_SECONDS);
        this.schedulePresencePing(nextDelay);
        if (resetActivity) {
          this.resetActivity();
        }
      } catch (error) {
        console.warn('⚠️ Error enviando presence ping:', error?.response || error);
        if (error?.response?.status === 401) {
          await this.performLogout();
          return;
        }
        this.schedulePresencePing(DEFAULT_TIMEOUT_SECONDS);
      }
    },
    async handleContinue() {
      this.warningVisible = false;
      this.resetActivity();
      await this.sendPresencePing({ resetActivity: true });
    },
    async handleLogout() {
      this.warningVisible = false;
      await this.performLogout();
    },
    async performLogout() {
      await this.$store.dispatch('user/setLogout');
      try {
        await router.push('/');
      } catch (navigationError) {
        console.warn('⚠️ Error redirigiendo tras logout:', navigationError);
      }
    },
    subscribeUserStatusChannel() {
      const userId = this.currentUserId;
      if (!userId) {
        return;
      }
      const channelName = `${USER_STATUS_CHANNEL_PREFIX}${userId}`;
      if (this.userStatusChannelName === channelName) {
        return;
      }
      this.leaveUserStatusChannel();
      try {
        if (typeof echoService.refreshAuthHeaders === 'function') {
          echoService.refreshAuthHeaders();
        }
        const channelFactory = typeof echoService.private === 'function' ? echoService.private.bind(echoService) : echoService.channel.bind(echoService);
        const channel = channelFactory(channelName);
        const handleTermination = payload => {
          this.handleRemoteTermination(payload);
        };
        channel
            .listen(USER_STATUS_EVENT, handleTermination)
            .listen(`.${USER_STATUS_EVENT}`, handleTermination)
            .listen(String.raw`.App\\Events\\${USER_STATUS_EVENT}`, handleTermination)
            .error(error => {
              console.warn('⚠️ Error en canal de estado de usuario:', error);
            });
        this.userStatusChannelName = channelName;
      } catch (subscriptionError) {
        console.warn('⚠️ No se pudo suscribir al canal de estado de usuario:', subscriptionError);
      }
    },
    leaveUserStatusChannel() {
      if (!this.userStatusChannelName) {
        return;
      }
      try {
        echoService.leave(this.userStatusChannelName);
      } catch (leaveError) {
        console.warn('⚠️ Error abandonando canal de estado de usuario:', leaveError);
      }
      this.userStatusChannelName = null;
    },
    async handleRemoteTermination(eventPayload = {}) {
      console.info('ℹ️ Sesión finalizada por el servidor.', eventPayload);
      this.warningVisible = false;
      await this.performLogout();
    }
  }
};
</script>

<style scoped>
.session-timeout-layer {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.session-timeout-modal {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  max-width: 420px;
  width: calc(100% - 32px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.session-timeout-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 18px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
